import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "never");

/** Duplicate order-number folders in "Order Photos" → photos copied into the linked order folder. */
export const DriveDuplicatesCard = ({ status }) => {
  const qc = useQueryClient();
  const s = status?.last_merge_stats;
  const scan = useMutation({
    mutationFn: async () => (await api.post("/drive/merge-duplicates")).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["drive-status"] });
      if (d?.error) return toast.error(d.error);
      toast.success(`Scan done — ${d.duplicate_numbers} duplicate number(s), ${d.photos_copied} photo(s) imported, ${d.folders_linked} folder(s) linked`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Scan failed"),
  });
  if (!status?.connected) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="drive-duplicates-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🗂️</span>
            <h2 className="font-semibold text-lg">Duplicate order folders</h2>
            <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">Hourly</Badge>
          </div>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            When <strong>Order Photos</strong> holds two folders for the same order number, the photos from the extra folder are
            copied into the order's linked folder so they show in the order's Photos tab. Nothing is deleted from Drive; a note is
            added to the order. Runs with every Drive sync.
          </p>
          <div className="text-xs text-slate-500 mt-3" data-testid="drive-duplicates-stats">
            Last scan: <strong>{fmt(status.last_merge_at)}</strong>
            {s && (
              <>
                {" "}· duplicate numbers: <strong data-testid="dup-numbers">{s.duplicate_numbers}</strong>
                {" "}· photos imported: <strong data-testid="dup-copied">{s.photos_copied}</strong>
                {" "}· folders linked: <strong data-testid="dup-linked">{s.folders_linked}</strong>
                {s.test_folders_removed > 0 && <> · test folders removed: <strong>{s.test_folders_removed}</strong></>}
                {s.unmatched?.length > 0 && (
                  <span className="block mt-1 text-amber-700" data-testid="dup-unmatched">
                    No CRM order for: {s.unmatched.map((g) => g.join(" / ")).join(" · ")}
                  </span>
                )}
                {s.errors?.length > 0 && <span className="block mt-1 text-red-700">Errors: {s.errors.join(" · ")}</span>}
              </>
            )}
          </div>
        </div>
        <Button variant="outline" onClick={() => scan.mutate()} disabled={scan.isPending} data-testid="drive-duplicates-scan-now">
          🔍 {scan.isPending ? "Scanning…" : "Scan now"}
        </Button>
      </div>
    </div>
  );
};
